/*
 * The basket puts a suit back together.
 *
 * A suit sold as three pieces reaches the cart as three separate lines - a
 * blazer, a waistcoat and a pair of trousers with nothing on screen to say
 * they are one thing. The customer chose "the Belmont" and the basket shows
 * them three garments and three prices, which reads as a mistake.
 *
 * Grouping is done here rather than in Liquid because the three lines are not
 * necessarily next to each other: quantity changes and re-adds move them, and
 * a group has to survive that. The theme rebuilds this list in place on every
 * cart change, so an observer regroups whatever it leaves behind.
 *
 * Nothing is removed or reordered destructively - the rows are the theme's own
 * and keep their merge keys, so the cart's own machinery still finds them.
 */
(function () {
  var SET = '[data-tc-set]';
  var GONE = '.merge-remove-wrapper';

  /*
   * A row on its way out is not part of the set any more.
   *
   * Removing a line does not delete its row: the theme parks it in a
   * .merge-remove-wrapper, collapses that, and deletes the wrapper when the
   * animation ends. Regrouping around a row in that state put the group box
   * inside the wrapper - and when the wrapper went, the whole suit went with
   * it, emptying a basket that still had two garments in it.
   */
  function leaving(row) {
    return !!(row.closest(GONE) || row.classList.contains('merge-remove-item'));
  }

  /**
   * What the rows are wearing — learned, not assumed.
   *
   * The heading used to be '£' plus the number, which is right until Shopify
   * Markets prices the basket in something else: a Swiss cart read CHF on
   * every line and £ on the set heading directly above them. Shopify's own
   * formatMoney is no help, since it carries the shop's format rather than the
   * market's and would put the £ straight back.
   *
   * So it copies the row it just read the price off: prefix and suffix both,
   * because not every currency goes in front of the number.
   */
  var SHAPE = null;

  /*
   * Where the separators sit is part of the shape too.
   *
   * A French cart writes "61,95 €" and a British one "£61.95": the comma there
   * is a decimal point, not a thousands mark, and reading it as one turned a
   * €255.85 suit into €25,585.00 — every price in every comma-decimal market
   * was out by a factor of a hundred, on the one line that claims to
   * summarise the whole set.
   *
   * Learned from the rows where they show it, and from the page's own language
   * where they do not: a basket of round hundreds shows no decimal mark at all,
   * and Intl knows what French does with one without a table of currencies
   * here that would need maintaining.
   */
  function localeSeparators() {
    var out = { decimal: '.', group: ',' };
    try {
      var lang = (typeof document !== 'undefined' && document.documentElement.lang) || 'en-GB';
      var parts = new Intl.NumberFormat(lang).formatToParts(1234.5);
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'decimal') out.decimal = parts[i].value;
        if (parts[i].type === 'group') out.group = parts[i].value;
      }
    } catch (e) { /* an old browser keeps the English pair */ }
    return out;
  }

  function money(pence) {
    var fallback = localeSeparators();
    var decimal = (SHAPE && SHAPE.decimal) || fallback.decimal;
    var group = (SHAPE && SHAPE.group) || fallback.group;
    var value = Math.abs(pence);
    var whole = String(Math.floor(value / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, group);
    var s = (pence < 0 ? '-' : '') + whole + decimal + ('0' + (value % 100)).slice(-2);
    if (SHAPE) return SHAPE.prefix + s + SHAPE.suffix;
    return '£' + s;
  }

  /**
   * The visible price, without the words only a screen reader was meant to
   * hear.
   *
   * Symmetry labels its prices with a hidden "Regular price" span inside the
   * same element, and textContent does not know the difference — so reading
   * the shape off the raw text put "Regular price £215.00" in the set heading.
   * The number always parsed correctly; it was the prefix that came back
   * carrying a sentence.
   */
  function priceText(el) {
    if (!el) return '';
    var clone = el.cloneNode(true);
    var hidden = clone.querySelectorAll('.visually-hidden, .visuallyhidden, .sr-only');
    for (var i = 0; i < hidden.length; i++) hidden[i].parentNode.removeChild(hidden[i]);
    return String(clone.textContent).replace(/\s+/g, ' ').trim();
  }

  /**
   * The line total of a row, in minor units, read off the theme's own markup.
   *
   * Stripping everything but digits and dots is what broke this: "61,95" came
   * back as 6195 and was multiplied by a hundred again. The separator that is
   * followed by one or two digits at the very end is the decimal one —
   * "61,95", "215.00", "1 234,56" — and everything before it groups
   * thousands, whatever character it happens to be. A price with no decimals
   * at all ("100 zł", "£1,234") is whole pounds, złoty or euros.
   */
  function priceOf(row) {
    var el = row.querySelector('.theme-money, .cart-item__price, [class*="price"]');
    var text = priceText(el);
    var run = text.match(/\d[\d.,\u00a0\u202f ]*\d|\d/);
    if (!run) return 0;

    var raw = run[0];
    var tail = raw.match(/([.,])(\d{1,2})$/);
    var decimal = tail ? tail[1] : null;
    var cents = tail ? (tail[2] + '0').slice(0, 2) : '00';
    var whole = tail ? raw.slice(0, raw.length - tail[0].length) : raw;
    var digits = whole.replace(/\D/g, '');
    if (!digits && !tail) return 0;

    /* The prefix and suffix come from the first row and stay; the separators
       keep being learned from every row after it, because the row that shows
       a thousands mark may not be the first one in the basket. */
    if (!SHAPE) {
      SHAPE = {
        prefix: text.slice(0, run.index),
        suffix: text.slice(run.index + raw.length),
        decimal: null,
        group: null,
      };
    }
    if (!SHAPE.decimal && decimal) SHAPE.decimal = decimal;
    if (!SHAPE.group) {
      var separators = whole.match(/[.,\u00a0\u202f ]/g) || [];
      for (var i = 0; i < separators.length; i++) {
        if (separators[i] !== decimal) { SHAPE.group = separators[i]; break; }
      }
    }
    return Number(digits || '0') * 100 + Number(cents);
  }

  /* The three words this script says, in the page's language: the cart
     writes them into data-labels on the list from the theme's locale files,
     which locales-sync.js fills from the table the apps and the admin panel
     share. English is the floor. The translation filter HTML-escapes, so the
     entities are undone. */
  function labelsOf(node) {
    var out = {};
    try {
      var raw = JSON.parse((node && node.getAttribute('data-labels')) || '{}');
      for (var k in raw) if (Object.prototype.hasOwnProperty.call(raw, k) && typeof raw[k] === 'string') {
        out[k] = raw[k].replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
      }
    } catch (e) { /* no labels: English */ }
    return out;
  }
  function tr(L, key, fallback) {
    var v = L[key];
    var out = (typeof v === 'string' && v && v.indexOf('translation missing') !== 0) ? v : fallback;
    for (var i = 3; i < arguments.length; i++) out = out.replace('%s', arguments[i]);
    return out;
  }

  function group(list) {
    var L = labelsOf(list);
    if (!list) return;

    /* Anything we built last time comes apart first: the rows inside it may
       have been replaced, removed, or joined by a fourth. Rebuilding from the
       rows themselves is the only version that cannot drift. */
    [].slice.call(list.querySelectorAll('.tc-cartset')).forEach(function (old) {
      /* Rows come out beside the wrapper, not inside it, in case an older
         pass left a box somewhere the theme is about to delete. */
      var anchor = old.closest(GONE) || old;
      var parent = anchor.parentNode;
      if (!parent) return;
      while (old.firstChild) {
        var node = old.firstChild;
        if (node.classList && node.classList.contains('tc-cartset__head')) { old.removeChild(node); continue; }
        parent.insertBefore(node, anchor);
      }
      if (old.parentNode) old.parentNode.removeChild(old);
    });

    var rows = [].slice.call(list.querySelectorAll('[data-merge-list-item]'));

    /* Every piece in the basket, with the set it says it belongs to. */
    var pieces = [];
    rows.forEach(function (row) {
      if (leaving(row)) return;
      var item = row.querySelector(SET);
      if (!item) return;
      var parts = (item.getAttribute('data-tc-parts') || '').split(',').filter(Boolean);
      pieces.push({
        row: row,
        handle: item.getAttribute('data-tc-handle') || '',
        key: item.getAttribute('data-tc-set'),
        name: item.getAttribute('data-tc-set-name') || tr(L, 'suit', 'Suit'),
        parts: parts,
      });
    });

    var seen = [];
    var byKey = {};
    var claimed = [];

    /*
     * The suits that are actually here, before anything else.
     *
     * A piece can belong to more than one suit - the Viceroy blue blazer is
     * sold in its own Prince of Wales set and again beside a Harris brown
     * waistcoat - and suit_set.suit names only one of them. Grouping by that
     * pointer split a complete suit in two: blazer and trousers under the
     * mix-and-match parent they point at, the blue waistcoat left below as a
     * loose garment, and a heading that read "2 pieces" over three.
     *
     * So a piece is asked what its set consists of rather than who its parent
     * is. Any one of them names all three, so a set is here when each handle it
     * names is on a row - and the row that named it is the one whose parent
     * gives the group its title. A piece is spent once, so the same blazer
     * cannot appear in two suits.
     */
    pieces.forEach(function (piece) {
      if (!piece.parts.length || byKey[piece.key]) return;
      var picked = [];
      var ok = piece.parts.every(function (handle) {
        for (var i = 0; i < pieces.length; i++) {
          var other = pieces[i];
          if (other.handle !== handle) continue;
          if (claimed.indexOf(other) > -1 || picked.indexOf(other) > -1) continue;
          picked.push(other);
          return true;
        }
        return false;
      });
      if (!ok || picked.length < 2) return;
      picked.forEach(function (p) { claimed.push(p); });
      /* Back into the order the basket has them in. The group is inserted where
         its first row sits, so "first" has to mean the highest one on the page
         and not whichever piece the set happened to name first. */
      picked.sort(function (a, b) { return pieces.indexOf(a) - pieces.indexOf(b); });
      byKey[piece.key] = { rows: picked.map(function (p) { return p.row; }), name: piece.name };
      seen.push(piece.key);
    });

    /*
     * Then whatever is left, by the parent it points at.
     *
     * Half a suit is still worth showing as half a suit: a man who has put the
     * blazer and the trousers in his basket and not the waistcoat should see
     * them together, under the name of the suit they came from.
     */
    pieces.forEach(function (piece) {
      if (claimed.indexOf(piece) > -1) return;
      var key = piece.key;
      if (!byKey[key]) { byKey[key] = { rows: [], name: piece.name }; seen.push(key); }
      byKey[key].rows.push(piece.row);
    });

    seen.forEach(function (key) {
      var set = byKey[key];
      /* One piece on its own is not a set. A man who removed the waistcoat has
         a blazer and trousers, and calling that "the Belmont suit" over one
         price would be telling him he is buying something he is not. */
      if (set.rows.length < 2) return;

      var box = document.createElement('div');
      box.className = 'tc-cartset';
      box.setAttribute('data-tc-cartset', key);

      var total = set.rows.reduce(function (a, r) { return a + priceOf(r); }, 0);
      var head = document.createElement('div');
      head.className = 'tc-cartset__head';
      head.innerHTML =
        '<span class="tc-cartset__kind">' + (set.rows.length === 3 ? tr(L, 'three_piece', '3 piece suit') : tr(L, 'n_pieces', '%s pieces', set.rows.length)) + '</span>' +
        '<span class="tc-cartset__name"></span>' +
        '<span class="tc-cartset__total">' + money(total) + '</span>';
      head.querySelector('.tc-cartset__name').textContent = set.name;

      /* Every kept row is a child of the list by now; anything else means the
         theme is mid-edit, and the next pass can group it. */
      var first = set.rows[0];
      if (first.parentNode !== list) return;
      list.insertBefore(box, first);
      box.appendChild(head);
      set.rows.forEach(function (r) { box.appendChild(r); });
    });
  }

  function watch() {
    var list = document.querySelector('[data-merge-list="cart-items"]');
    if (!list) return false;
    group(list);
    new MutationObserver(function () {
      /* Regrouping edits the list, which would call this again. The flag is
         cheaper than disconnecting and reconnecting around every pass. */
      if (watch.busy) return;
      watch.busy = true;
      try { group(list); } finally { setTimeout(function () { watch.busy = false; }, 0); }
    }).observe(list, { childList: true, subtree: true });
    return true;
  }

  if (!watch()) {
    /* The drawer is rendered with the page but the list can arrive with the
       first cart change on a page that started empty. */
    var tries = 0;
    var timer = setInterval(function () {
      if (watch() || ++tries > 40) clearInterval(timer);
    }, 250);
  }
})();
